import { useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query'
import { AxiosError } from 'axios'
import { isEqual } from 'lodash'

import { useNotificationContext } from 'shared/context/Notifications'
import isAxiosError from 'shared/utils/helpers/isAxiosError'

import {
   fetchWholesalers,
   fetchWholesalerDeliveryTimes,
   editWholesalerDeliveryTimes,
   deleteWholesaler,
   addWholesaler,
   askForCooperation,
   editWholesaler,
   resendWholesalerCoopMail,
} from './services'
import {
   AddWholesalerPayload,
   AskCooperationPayload,
   DeleteWholesalerPayload,
   EditDeliveryTimesPayload,
   StateType,
} from './types'

export default function useWholesalers(locationId?: string): UseQueryResult<StateType> {
   return useQuery(['wholesalers', 'list', locationId], () => fetchWholesalers(locationId), {
      initialData: [],
      enabled: !!locationId,
   })
}

export function useDeliveryTimesQuery(cooperationUuid: string, locationUuid: string) {
   return useQuery(['wholesalers', 'times', cooperationUuid, locationUuid], () =>
      fetchWholesalerDeliveryTimes(cooperationUuid, locationUuid)
   )
}

export function useDeliveryTimesMutation() {
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()
   return useMutation<
      Result & Pick<EditDeliveryTimesPayload, 'cooperationUuid'>,
      AxiosError<ErrorType> | Error,
      EditDeliveryTimesPayload,
      unknown
   >(
      async (payload) => {
         const { status } = await editWholesalerDeliveryTimes(payload)
         return {
            status,
            cooperationUuid: payload.cooperationUuid,
         }
      },
      {
         onSuccess: (data) => {
            if (data.status === 204) {
               queryClient.invalidateQueries(['wholesalers', 'times', data.cooperationUuid])
               addNotification('successSave', 'success')
            }
         },
         onError: (error) => {
            if (error) {
               if (isAxiosError<ErrorType>(error)) {
                  addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
               } else {
                  addNotification('failedSave', 'error')
               }
            }
         },
      }
   )
}

export function useDeleteCooperationMutation() {
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   return useMutation<Result, AxiosError<ErrorType> | Error, DeleteWholesalerPayload, unknown>(
      (payload) => deleteWholesaler(payload),
      {
         onSuccess: (data) => {
            if (data.status === 204) {
               queryClient.invalidateQueries('wholesalers')
               addNotification('successSave', 'success')
            }
         },
         onError: (error) => {
            if (error) {
               if (isAxiosError<ErrorType>(error)) {
                  addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
               } else {
                  addNotification('failedSave', 'error')
               }
            }
         },
      }
   )
}

export function useResendCooperationEmailMutation() {
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   return useMutation<
      Result,
      AxiosError<ErrorType> | Error,
      { locationUuid: string; cooperationUuid: string },
      unknown
   >(({ locationUuid, cooperationUuid }) => resendWholesalerCoopMail(locationUuid, cooperationUuid), {
      onSuccess: (data) => {
         if (data.status === 204) {
            queryClient.invalidateQueries('wholesalers')
            addNotification('successSave', 'success')
         }
      },
      onError: (error) => {
         if (error) {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
            } else {
               addNotification('failedSave', 'error')
            }
         }
      },
   })
}

export function useAddCooperationMutation() {
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   return useMutation<
      Result,
      AxiosError<ErrorType> | Error,
      { locationUuid: string; payload: AddWholesalerPayload },
      unknown
   >(({ locationUuid, payload }) => addWholesaler(locationUuid, payload), {
      onSuccess: (data) => {
         if (data.status === 204) {
            queryClient.invalidateQueries('wholesalers')
            addNotification('successSave', 'success')
         }
      },
      onError: (error) => {
         if (error) {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(
                  error.response?.data?.errors[0].code ||
                     error.response?.data?.errors[0].defaultMessage ||
                     'failedSave',
                  'error'
               )
            } else {
               addNotification('failedSave', 'error')
            }
         }
      },
   })
}

export function useEditCooperationMutation() {
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   return useMutation<
      Result,
      Error | AxiosError<ErrorType> | Error,
      { locationUuid: string; cooperationUuid: string; payload: AddWholesalerPayload },
      unknown
   >(
      ({ locationUuid, cooperationUuid, payload }) => {
         const initialData = queryClient.getQueryData<StateType>(['wholesalers', 'list', locationUuid])

         const allWholesalers = initialData?.cooperating
            .concat(initialData.notCooperating.CLIENT_DATA)
            .concat(initialData.notCooperating.CLIENT_ONLY)
            .concat(initialData.notCooperating.COOPERATION_EMAIL)

         const wholesalerToChange = allWholesalers?.find(
            (wholesaler) => wholesaler.wholesaleUuid === payload.wholesaleUuid
         )
         const departmentUuids: string[] = []
         departmentUuids[0] =
            wholesalerToChange?.departments.find((item) => item.type === 'FIRST')?.departmentUuid || ''
         departmentUuids[1] =
            wholesalerToChange?.departments.find((item) => item.type === 'SECOND')?.departmentUuid || ''
         departmentUuids[2] =
            wholesalerToChange?.departments.find((item) => item.type === 'THIRD')?.departmentUuid || ''
         const initialPayload = wholesalerToChange
            ? {
                 additionalParameter: wholesalerToChange.additionalParameter,
                 deliveryDepartment: wholesalerToChange.deliveryDepartment,
                 departmentUuids,
                 locationUuid: wholesalerToChange.locationUuid,
                 login: wholesalerToChange.login,
                 orderDocumentType: wholesalerToChange.orderDocumentType,
                 password: wholesalerToChange.password,
                 wholesaleUuid: wholesalerToChange.wholesaleUuid,
                 minLogistics: wholesalerToChange.minLogistics,
                 cooperationUuid: wholesalerToChange.cooperationUuid,
              }
            : {}
         if (isEqual(initialPayload, payload)) {
            throw new Error('apiErrors.nothingChanged')
         } else {
            return editWholesaler(locationUuid, cooperationUuid, payload)
         }
      },
      {
         onSuccess: (data) => {
            if (data.status === 204) {
               queryClient.invalidateQueries('wholesalers')
               queryClient.invalidateQueries('minimums')
               addNotification('successSave', 'success')
            }
         },
         onError: (error) => {
            if (error) {
               if (isAxiosError<ErrorType>(error)) {
                  addNotification(
                     error.response?.data?.errors[0].code ||
                        error.response?.data?.errors[0].defaultMessage ||
                        'failedSave',
                     'error'
                  )
               } else {
                  addNotification('failedSave', 'error')
               }
            }
         },
      }
   )
}

export function useAskForCoopMutation() {
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   return useMutation<
      Result,
      AxiosError<ErrorType> | Error,
      { locationUuid: string; payload: AskCooperationPayload },
      unknown
   >(({ locationUuid, payload }) => askForCooperation(locationUuid, payload), {
      onSuccess: (data) => {
         if (data.status === 204) {
            queryClient.invalidateQueries('wholesalers')
            addNotification('coopMailSendSuccess', 'success')
         }
      },
      onError: (error) => {
         if (error) {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
            } else {
               addNotification('failedSave', 'error')
            }
         }
      },
   })
}
